import React from "react";
import { useNavigate } from "react-router-dom";
import { PageHero, Section } from "@/components/PageParts";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useSettings } from "@/context/SettingsContext";
import { Search, Network, ClipboardList, HeartHandshake, TrendingUp, ShieldCheck } from "lucide-react";

const STEPS = [
  [Search, "Identify private-pay needs", "Learn to recognize private-pay healthcare and support-service needs in your market and route them appropriately — without collecting unnecessary protected health information."],
  [Network, "Develop provider networks", "Recruit and vet participating providers across many service categories and track their available care capacity."],
  [HeartHandshake, "Build referral relationships", "Develop professional referral sources — hospitals, discharge planners, care managers, attorneys, and more."],
  [ClipboardList, "Intake, match & follow up", "Run a compliant, minimal-PHI intake, match consumers to available capacity, and follow up with disciplined automation."],
  [TrendingUp, "Earn on qualifying transactions", "Compensation is defined by approved written provider agreements and applicable law — always private-pay first."],
  [ShieldCheck, "Stay compliant", "Automatic compliance gates flag government-payer and sensitive situations for human review."],
];

export default function HowItWorks() {
  const nav = useNavigate();
  const settings = useSettings();
  return (
    <div>
      <PageHero eyebrow="How it works" title="A system for private-pay placement"
        subtitle="Care Placement Pro combines structured education, an agent network, a provider marketplace, and AI automation — designed to require as little repetitive human intervention as reasonably possible." />
      <Section className="py-20">
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {STEPS.map(([Icon, t, d], i) => (
            <Card key={t} className="p-7" data-testid={`how-step-${i}`}>
              <div className="flex items-center gap-3">
                <span className="font-heading text-2xl font-bold text-slate-200">0{i + 1}</span>
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky">
                  <Icon className="h-5 w-5 text-clinical" />
                </div>
              </div>
              <h3 className="mt-4 font-heading text-lg font-semibold text-navy">{t}</h3>
              <p className="mt-2 text-sm text-slate-500">{d}</p>
            </Card>
          ))}
        </div>
        <Card className="mt-12 flex flex-col items-center gap-5 bg-navy p-10 text-center text-white">
          <h2 className="font-heading text-2xl font-semibold lg:text-3xl">Ready to learn the system?</h2>
          <Button data-testid="how-join-btn" size="lg" onClick={() => nav("/join")} className="bg-clinical hover:bg-clinical/90">
            Join Care Placement Pro — {settings.membership_price}
          </Button>
        </Card>
      </Section>
    </div>
  );
}
